// src/pages/supplyList/SupplyFilterSection.jsx
import React, { useMemo } from 'react';
import {
  Box, TextField, Button, Autocomplete, Collapse, Divider
} from '@mui/material';

/**
 * 🚀 供給履歴フィルターセクション
 * 製品ID送信対応版
 */
const SupplyFilterSection = ({
  showFilter,
  supplies,
  clientFilter,
  setClientFilter,
  productFilter, // 製品オブジェクト {id, name} を保持
  setProductFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onApplyFilter,
  onResetFilter,
  loading = false
}) => {

  // 🔹 顧客選択肢の生成（メモ化で最適化）
  const uniqueClients = useMemo(() => {
    if (!Array.isArray(supplies)) return [];
    
    const clientMap = new Map();
    
    supplies.forEach(supply => {
      if (supply.clientName && supply.clientId) {
        clientMap.set(supply.clientId, supply.clientName);
      }
    });
    
    return Array.from(clientMap.entries()).map(([id, name]) => ({
      id: Number(id),
      name: name
    }));
  }, [supplies]);

  // 🔹 製品選択肢の生成（ID+名前のオブジェクト形式）
  const productOptions = useMemo(() => {
    if (!Array.isArray(supplies)) return [];
    
    const productMap = new Map();
    
    supplies.forEach(supply => {
      // 新しいClientProduct形式での製品情報取得
      if (supply.productId && supply.productName) {
        productMap.set(supply.productId, {
          id: Number(supply.productId),
          name: supply.productName
        });
      }
      // 後方互換性: 旧形式での製品名のみの場合
      else if (supply.productName && !supply.productId) {
        // 製品IDがない場合は製品名をキーとして使用（非推奨だが互換性のため）
        const fallbackKey = `legacy_${supply.productName}`;
        if (!productMap.has(fallbackKey)) {
          productMap.set(fallbackKey, {
            id: null,
            name: supply.productName
          });
        }
      }
    });
    
    return Array.from(productMap.values())
      .filter(product => product.name) // 名前がないものは除外
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [supplies]);

  return (
    <Collapse in={showFilter}>
      {/* フィルター入力エリア */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: '1fr 1fr', 
          md: 'repeat(4, 1fr)' 
        },
        gap: 2,
        mb: 2
      }}>
        
        {/* 顧客選択 */}
        <Autocomplete
          options={uniqueClients}
          getOptionLabel={(option) => option.name || ''}
          value={clientFilter}
          onChange={(event, newValue) => setClientFilter(newValue)}
          isOptionEqualToValue={(option, value) => 
            option.id === value?.id
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="顧客名で検索"
              placeholder="顧客名を入力"
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          noOptionsText="該当する顧客がありません"
          loadingText="検索中..."
          clearOnBlur
          clearOnEscape
          disabled={loading}
        />
        
        {/* 製品選択（ID対応版） */}
        <Autocomplete
          options={productOptions}
          getOptionLabel={(option) => option.name || ''}
          value={productFilter}
          onChange={(event, newValue) => setProductFilter(newValue)}
          isOptionEqualToValue={(option, value) => 
            option.id === value?.id
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="製品名で検索" 
              placeholder="製品名を入力"
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id || option.name}>
              {option.name}
            </li>
          )}
          noOptionsText="該当する製品がありません"
          loadingText="検索中..."
          disabled={loading || productOptions.length === 0}
        />
        
        {/* 開始日 */}
        <TextField
          type="date"
          label="開始日"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          disabled={loading}
          inputProps={{
            max: new Date().toISOString().split('T')[0]
          }}
        />
        
        {/* 終了日 */}
        <TextField
          type="date"
          label="終了日"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          disabled={loading}
          inputProps={{
            max: new Date().toISOString().split('T')[0],
            min: startDate || undefined
          }}
        />
      </Box>
      
      {/* フィルターアクションボタン */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button 
          variant="contained" 
          size="small" 
          onClick={onApplyFilter}
          disabled={loading}
        >
          適用
        </Button>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={onResetFilter}
          disabled={loading}
        >
          リセット
        </Button>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
    </Collapse>
  );
};

export default SupplyFilterSection;